/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { DataGrid } from '@mui/x-data-grid';
import { withTheme } from '@emotion/react';
import { useSelector } from 'react-redux';
import { TABLE_HEADER_FONT_COLOR } from 'modules/common/constants/style';
import { selectModelUsage, selectUsageLoader } from '../../selectors';

const columnVisibilityModel = {
  id: false,
};

const formatCost = ({ value }) =>
  value === null || value === undefined ? 'N/A' : `$${Number(value).toFixed(4)}`;

const costColumn = (field, headerName) => ({
  field,
  headerName,
  headerClassName: 'super-app-theme--header',
  flex: 1,
  valueGetter: ({ row }) => row.cost?.[field],
  valueFormatter: formatCost,
});
//
const columns = [
  {
    field: 'id',
    headerName: 'ID',
    headerClassName: 'super-app-theme--header',
    flex: 1,
  },
  {
    field: 'provider',
    headerName: 'Provider',
    headerClassName: 'super-app-theme--header',
    flex: 1,
  },
  {
    field: 'model',
    headerName: 'Model',
    headerClassName: 'super-app-theme--header',
    flex: 1,
  },
  {
    field: 'requestCount',
    headerName: 'Requests',
    headerClassName: 'super-app-theme--header',
    flex: 1,
  },
  {
    field: 'inputTokenCount',
    headerName: 'Input tokens',
    headerClassName: 'super-app-theme--header',
    flex: 1,
    valueGetter: ({ row }) => row.tokenUsage?.inputTokenCount ?? 0,
  },
  {
    field: 'outputTokenCount',
    headerName: 'Output tokens',
    headerClassName: 'super-app-theme--header',
    flex: 1,
    valueGetter: ({ row }) => row.tokenUsage?.outputTokenCount ?? 0,
  },
  {
    field: 'cachedTokenCount',
    headerName: 'Cached tokens',
    headerClassName: 'super-app-theme--header',
    flex: 1,
    valueGetter: ({ row }) => row.tokenUsage?.cachedTokenCount ?? 0,
  },
  {
    field: 'totalTokenCount',
    headerName: 'Total tokens',
    headerClassName: 'super-app-theme--header',
    flex: 1,
    valueGetter: ({ row }) => row.tokenUsage?.totalTokenCount ?? 0,
  },
  {
    field: 'missingUsageCount',
    headerName: 'Missing usage',
    headerClassName: 'super-app-theme--header',
    flex: 1,
  },
  {
    field: 'missingCostCount',
    headerName: 'Missing cost',
    headerClassName: 'super-app-theme--header',
    flex: 1,
  },
  costColumn('inputCost', 'Input cost (USD)'),
  costColumn('outputCost', 'Output cost (USD)'),
  costColumn('cachedCost', 'Cached cost (USD)'),
  costColumn('cacheWriteCost', 'Cache write cost (USD)'),
  costColumn('totalCost', 'Cost (USD)'),
];
//
const LlmModelUsageTableView = () => {
  const usage = useSelector(selectModelUsage);
  const usageLoading = useSelector(selectUsageLoader);

  const rows = (usage?.models ?? []).map((m) => ({
    ...m,
    id: `${m.provider}/${m.model}`,
  }));
  //
  return (
    <div style={{ height: 500, flex: 1 }}>
      <DataGrid
        sx={{
          '& .super-app-theme--header': {
            backgroundColor: 'white',
            color: TABLE_HEADER_FONT_COLOR,
          },
          '& .MuiDataGrid-main': {
            backgroundColor: 'white',
          },
          '.MuiDataGrid-footerContainer': {
            backgroundColor: 'white',
          },
        }}
        columnVisibilityModel={columnVisibilityModel}
        rowHeight={50}
        rows={rows}
        columns={columns}
        loading={usageLoading}
        disableSelectionOnClick
      />
    </div>
  );
};
//
export default withTheme(LlmModelUsageTableView);
